import { Spinner } from "@elit-hair/elit-ui";
import { ReactNode, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";

import { useSingleInstance } from "@/providers/single-instance-checker/use-single-instance";

type SingleInstanceContextType = {
    isMultipleInstances: boolean;
    isInitialized: boolean;
};

const SingleInstanceContext = createContext<SingleInstanceContextType | undefined>(undefined);

type SingleInstanceProviderProps = {
    children: ReactNode;
    alertComponent?: ReactNode; // Optional UI wrapper for multiple-instance alert
};

/**
 * Provider that manages single instance logic for the application
 * This provider only handles the core logic and can be reused across different projects
 */
export const SingleInstanceProvider = ({ children, alertComponent }: SingleInstanceProviderProps) => {
    const singleInstanceData = useSingleInstance();
    const { isInitialized } = singleInstanceData;
    const { t } = useTranslation();

    // Render a minimal blocking loader until single-instance state is ready
    if (!isInitialized) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex flex-col items-center gap-2">
                    <Spinner />
                    <p className="text-muted-foreground">{t("checking_instance")}</p>
                </div>
            </div>
        );
    }
    return (
        <SingleInstanceContext.Provider value={singleInstanceData}>
            {children}
            {alertComponent ? <>{alertComponent}</> : null}
        </SingleInstanceContext.Provider>
    );
};

/**
 * Hook to access single instance context
 */
export const useSingleInstanceContext = () => {
    const context = useContext(SingleInstanceContext);
    if (context === undefined) {
        throw new Error("useSingleInstanceContext must be used within a SingleInstanceProvider");
    }
    return context;
};
